import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useRef, useEffect } from 'react';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import { ICatalogSectionItemBlock } from '@features/cms/typescript/index';
import styles from '@features/cms/components/pages/CatalogSectionBlocks/CatalogSectionBlocks.styles';
import scrollToElementById from '@utils/scrollToElementById';
import RenderMedia from '@utils/RenderMedia';
type PropsType = {
  item: ICatalogSectionItemBlock;
  layout: string;
  isFirst: boolean;
  getHoverImage: () => string;
  getTextColor: () => string;
  getTextHoverColor: () => string;
  getBgColor: () => string;
  getBgHoverColor: () => string;
  getImage: () => string;
  getVideo: () => string;
};
const CatalogSectionItemDefault = ({
  item,
  layout,
  isFirst,
  getHoverImage,
  getTextColor,
  getTextHoverColor,
  getBgColor,
  getBgHoverColor,
  getImage,
  getVideo
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    header,
    description,
    lineSpacing,
    image,
    filePath,
    fileTarget,
    video,
    buttonVariant,
    animation,
    buttonText
  } = item;
  const containerRef = useRef<HTMLDivElement>(null);
  const linkRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null);

  // WCAG 2.4.7 Focus Visible - dodaj klasę na kontenerze gdy link/button ma focus
  useEffect(() => {
    const container = containerRef.current;
    const link = linkRef.current;
    if (!container || !link) return;
    const handleFocusIn = () => {
      if (link.matches(':focus-visible')) {
        container.classList.add('has-focus-visible');
      }
    };
    const handleFocusOut = () => {
      container.classList.remove('has-focus-visible');
    };
    link.addEventListener('focusin', handleFocusIn);
    link.addEventListener('focusout', handleFocusOut);
    return () => {
      link.removeEventListener('focusin', handleFocusIn);
      link.removeEventListener('focusout', handleFocusOut);
    };
  }, []);
  const renderContent = (isLink: boolean) => <>
      <div sx={{
      ...styles.catalogCard(isLink, getHoverImage() === 'none' && !!(header || description), 0, getTextColor(), getBgHoverColor()),
      ...styles.descriptionContainer(getTextHoverColor(), lineSpacing),
      backgroundImage: getHoverImage()
    }} data-testid="catalog-section-item-default">
        <Heading h={3} headingType={3} color={getTextHoverColor()} mobile>
          {header}
        </Heading>
        <div style={{
        color: getTextHoverColor()
      }} dangerouslySetInnerHTML={{
        __html: description || ''
      }} data-testid="catalog-section-item-default-content" />
      </div>
      {filePath && <div sx={styles.styledViewFileLink(getTextColor(), buttonVariant !== 'noIcon')} data-testid="catalog-section-item-default-button">
          {buttonVariant !== 'noIcon' && <Icon icon="Download2" />}
          <Heading h={6} headingType={6} whiteSpace="nowrap" color={getTextHoverColor()} uppercase mobile>
            {buttonText || t('download')}
          </Heading>
        </div>}
    </>;
  return <div ref={containerRef} sx={{
    ...styles.styledLayout(layout, true, 'default', getTextColor(), getTextHoverColor(), getBgColor(), getBgHoverColor(), animation || undefined).item,
    backgroundImage: !getVideo() ? getImage() : 'none'
  }} data-testid="catalog-section-item-default-container">
      <div sx={styles.heading()} data-testid="catalog-section-item-default-heading">
        <Heading h={isFirst ? 1 : 3} headingType={3} color={getTextColor()} mobile>
          {header}
        </Heading>
      </div>

      {RenderMedia(image, video, true, undefined, header, false)}
      {filePath ? <>
          {filePath.slice(0, 1) === '#' ? <button ref={linkRef as React.RefObject<HTMLButtonElement>} type="button" onClick={() => scrollToElementById(filePath.substring(1))} data-testid={`catalogSectionItem-${header}-button`}>
              {renderContent(true)}
            </button> : <Link ref={linkRef as React.RefObject<HTMLAnchorElement>} href={filePath} target={fileTarget || '_blank'} rel={!fileTarget || fileTarget === '_blank' ? 'noopener noreferrer' : ''} data-testid="catalog-section-item-default-link">
              {renderContent(true)}
            </Link>}
        </> : <span>{renderContent(false)}</span>}
    </div>;
};
export default CatalogSectionItemDefault;